import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { templatesAPI } from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Save, Download, ArrowLeft, Upload, FileArchive } from 'lucide-react';

const EmailBuilderUnlayer = () => {
  const { id } = useParams();
  const emailEditorRef = useRef(null);
  const [templateName, setTemplateName] = useState('Untitled Template');
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Load Unlayer script
    const script = document.createElement('script');
    script.src = 'https://editor.unlayer.com/embed.js';
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      // Initialize Unlayer
      window.unlayer.init({
        id: 'editor-container',
        projectId: 0, // You can set your project ID from Unlayer dashboard
        displayMode: 'email',
        appearance: {
          theme: 'dark',
          panels: {
            tools: {
              dock: 'left'
            }
          }
        },
        features: {
          preview: true,
          imageEditor: true,
          textEditor: {
            spellChecker: true
          }
        }
      });

      emailEditorRef.current = window.unlayer;

      // Load existing template if editing
      if (id) {
        loadTemplate(id);
      }
    };

    return () => {
      document.body.removeChild(script);
    };
  }, [id]);

  const loadTemplate = async (templateId) => {
    try {
      const response = await templatesAPI.getOne(templateId);
      const template = response.data;
      setTemplateName(template.name);
      
      // Load design into Unlayer
      if (template.json_data && emailEditorRef.current) {
        try {
          const design = JSON.parse(template.json_data);
          emailEditorRef.current.loadDesign(design);
        } catch (e) {
          // If json_data is not Unlayer format, load as HTML
          if (template.html) {
            emailEditorRef.current.loadDesign({
              body: {
                rows: [{
                  cells: [1],
                  columns: [{
                    contents: [{
                      type: 'html',
                      values: {
                        html: template.html
                      }
                    }]
                  }]
                }]
              }
            });
          }
        }
      }
    } catch (error) {
      toast.error('Failed to load template');
    }
  };

  const handleSave = async () => {
    if (!emailEditorRef.current) return;
    setSaving(true);

    try {
      emailEditorRef.current.exportHtml(async (data) => {
        const { design, html } = data;

        const templateData = {
          name: templateName,
          html: html,
          json_data: JSON.stringify(design)
        };

        if (id) {
          await templatesAPI.update(id, templateData);
          toast.success('Template updated successfully');
        } else {
          const response = await templatesAPI.create(templateData);
          toast.success('Template created successfully');
          navigate(`/builder/${response.data.id}`);
        }

        setSaving(false);
      });
    } catch (error) {
      toast.error('Failed to save template');
      setSaving(false);
    }
  };

  const handleExportHTML = () => {
    if (!emailEditorRef.current) return;

    emailEditorRef.current.exportHtml((data) => {
      const { html } = data;
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${templateName}.html`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('HTML exported successfully');
    });
  };

  const handleZIPImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.zip')) {
      toast.error('Please select a ZIP file');
      return;
    }

    setImporting(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/templates/import-zip`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) throw new Error('Import failed');

      const template = await response.json();
      toast.success('ZIP imported successfully!');
      
      setTemplateName(template.name);
      
      // Load imported HTML into Unlayer
      if (emailEditorRef.current && template.html) {
        emailEditorRef.current.loadDesign({
          body: {
            rows: [{
              cells: [1],
              columns: [{
                contents: [{
                  type: 'html',
                  values: {
                    html: template.html
                  }
                }]
              }]
            }]
          }
        });
      }
      
      setTimeout(() => {
        navigate(`/builder/${template.id}`);
      }, 1000);
      
    } catch (error) {
      toast.error('Failed to import ZIP');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleZIPExport = async () => {
    if (!id) {
      toast.error('Please save the template first');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/templates/${id}/export-zip`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${templateName}.zip`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success('ZIP exported successfully!');
    } catch (error) {
      toast.error('Failed to export ZIP');
    }
  };

  return (
    <div className="h-screen flex flex-col bg-[#0B0C10]">
      {/* Top Toolbar */}
      <div className="flex items-center justify-between px-6 py-4 bg-[#13151A] border-b border-[#2A2D36]">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate('/templates')}
            data-testid="back-to-templates"
            variant="outline"
            size="sm"
            className="border-[#2A2D36] text-[#9CA3AF] hover:bg-[#1F2128] hover:text-[#F3F4F6]"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          <Input
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            data-testid="template-name-input"
            placeholder="Template Name"
            className="w-64 bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6]"
          />
        </div>

        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip"
            onChange={handleZIPImport}
            className="hidden"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            data-testid="import-zip-button"
            variant="outline"
            disabled={importing}
            size="sm"
            className="border-[#2A2D36] text-[#9CA3AF] hover:bg-[#1F2128] hover:text-[#F3F4F6]"
          >
            <Upload size={16} className="mr-2" />
            {importing ? 'Importing...' : 'Import ZIP'}
          </Button>
          <Button
            onClick={handleZIPExport}
            data-testid="export-zip-button"
            variant="outline"
            disabled={!id}
            size="sm"
            className="border-[#2A2D36] text-[#9CA3AF] hover:bg-[#1F2128] hover:text-[#F3F4F6]"
          >
            <FileArchive size={16} className="mr-2" />
            Export ZIP
          </Button>
          <Button
            onClick={handleExportHTML}
            data-testid="export-html-button"
            variant="outline"
            size="sm"
            className="border-[#2A2D36] text-[#9CA3AF] hover:bg-[#1F2128] hover:text-[#F3F4F6]"
          >
            <Download size={16} className="mr-2" />
            Export HTML
          </Button>
          <Button
            onClick={handleSave}
            data-testid="save-template-button"
            disabled={saving}
            className="bg-[#183871] hover:bg-[#1F4BA1] text-white shadow-[0_0_15px_rgba(24,56,113,0.3)]"
          >
            <Save size={16} className="mr-2" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Unlayer Editor Container */}
      <div id="editor-container" className="flex-1" style={{ minHeight: 'calc(100vh - 80px)' }} />
    </div>
  );
};

export default EmailBuilderUnlayer;
